import axios from './axiosConfig'
import type { ApiRequestOptions } from './requestOptions'

export type CustomerAddressSummary = {
 id: string
 label?: string | null
 address_line_1: string
 address_line_2?: string | null
 city?: string | null
 area?: string | null
 notes?: string | null
 is_default?: boolean
 delivery_zone_id?: string | null
 delivery_zone_name?: string | null
 delivery_fee?: number | null
 branch_id?: string | null
 last_used_at?: string | null
}

export type CustomerPhoneRow = {
 id: string
 phone: string
 label?: string | null
 is_primary?: boolean
 notes?: string | null
 created_at?: string
}

export type CustomerDeliveryRow = {
 id: string
 sale_id: number
 invoice_number?: string | null
 date?: string | null
 delivery_status?: string | null
 driver_name?: string | null
 delivery_address?: string | null
 delivery_phone?: string | null
 delivery_fee?: number | null
 customer_address_id?: string | null
 customer_address?: {
 id: string
 label?: string | null
 address_line_1?: string
 area?: string | null
 city?: string | null
 } | null
 delivery_zone_id?: string | null
 delivery_zone_name?: string | null
 delivery_zone_fee?: number | null
 order_total?: number | null
 branch_id?: string | null
 branch_name?: string | null
 collection_method?: string | null
}

export type CustomerRow = {
 id: number
 name: string
 phone: string
 primary_phone?: string
 secondary_phones_count?: number
 phone_numbers_summary?: string | null
 phones?: CustomerPhoneRow[]
 email?: string | null
 notes?: string | null
 branch_id?: string | null
 branch_name?: string | null
 balance?: number | null
 debt?: number | null
 credit_limit?: number | null
 points_balance?: number
 wallet_balance?: number | null
 sales_count?: number
 orders_count?: number
 last_order_at?: string | null
 latest_order?: {
 id: number
 invoice_number?: string | null
 sale_date?: string | null
 total?: number | null
 order_type?: string | null
 branch_id?: string | null
 } | null
 default_address?: CustomerAddressSummary | null
 addresses?: CustomerAddressSummary[]
 created_at?: string
 updated_at?: string
}

export type CustomerData = {
 name: string
 phone: string
 email?: string
 notes?: string
 branch_id?: string
 additional_phones?: Array<{ phone: string; label?: string; notes?: string }>
}

export type CustomerPhonePayload = {
 phone: string
 label?: string | null
 notes?: string | null
 is_primary?: boolean
}

export type CustomerAddressPayload = {
 label?: string | null
 address_line_1: string
 address_line_2?: string | null
 city?: string | null
 area?: string | null
 notes?: string | null
 is_default?: boolean
 delivery_zone_id?: string | null
}

export type PosCustomerLookupRow = {
 id: number
 name: string
 phone: string
 primary_phone?: string
 matched_phone?: string | null
 phones?: CustomerPhoneRow[]
 additional_phones: string[]
 email?: string | null
 notes?: string | null
 addresses: Array<{
 id: string
 label?: string | null
 address?: string
 address_line_1?: string
 address_line_2?: string | null
 city?: string | null
 area?: string | null
 zone_id?: string | null
 zone_name?: string | null
 delivery_fee?: number | null
 phone?: string | null
 is_default?: boolean
 last_used_at?: string | null
 }>
 latest_delivery_address?: {
 source: string
 customer_address_id?: string | null
 address_text?: string
 delivery_phone?: string | null
 delivery_zone_id?: string | null
 } | null
 latest_order?: {
 id: number
 invoice_number?: string | null
 sale_date?: string | null
 total: number
 branch_id: string
 } | null
 orders_count: number
 last_order_at?: string | null
}

export type PosCustomerLookupData = {
 exists: boolean
 exact_match: boolean
 customers: PosCustomerLookupRow[]
 duplicate_phone_warning: string | null
}

export type CustomerPaymentRow = {
 id: number
 amount: number
 type: 'in' | 'out' | string
 entry_type: 'debt_collection' | 'manual_debt' | 'debt_increase' | 'balance_credit' | 'other' | string
 payment_method?: string | null
 payment_date?: string | null
 notes?: string | null
 sale_id?: number | null
 invoice_number?: string | null
 vault_id?: string | null
 vault_name?: string | null
 created_at?: string | null
}

export const customersAPI = {
 getAll: async (params?: Record<string, unknown>, options?: ApiRequestOptions) => {
 const response = await axios.get('/customers', { params, ...options })
 return response.data
 },

 getById: async (id: number, options?: ApiRequestOptions) => {
 const response = await axios.get(`/customers/${id}`, options)
 return response.data
 },

 create: async (data: CustomerData) => {
 const response = await axios.post('/customers', data)
 return response.data
 },

 update: async (id: number, data: Partial<CustomerData>) => {
 const response = await axios.put(`/customers/${id}`, data)
 return response.data
 },

 delete: async (id: number) => {
 const response = await axios.delete(`/customers/${id}`)
 return response.data
 },

 getSales: async (id: number, params?: { per_page?: number; page?: number }) => {
 const response = await axios.get(`/customers/${id}/sales`, { params })
 return response.data
 },

 getDeliveries: async (id: number, params?: { per_page?: number; page?: number }) => {
 const response = await axios.get(`/customers/${id}/deliveries`, { params })
 return response.data
 },

 addBalance: async (id: number, amount: number, type: string, notes: string) => {
 const response = await axios.post(`/customers/${id}/balance`, { amount, type, notes })
 return response.data
 },

 getOpenDebts: async (id: number) => {
 const response = await axios.get(`/customers/${id}/open-debts`)
 return response.data as {
 status: string
 data?: {
 layaway_plans: Array<{ id: string; sale_id?: number | null; invoice_number?: string | null; remaining: number; next_due_date?: string | null }>
 credit_sales: Array<{ id: number; invoice_number?: string | null; remaining: number; sale_date?: string | null }>
 }
 }
 },

 recordDebtPayment: async (
 id: number,
 payload: {
  amount: number
  payment_method: string
  financial_account_id: string
  vault_id?: string | null
  sale_id?: number | null
  notes?: string
  idempotency_key?: string
 },
 ) => {
 const response = await axios.post(`/customers/${id}/debt-payments`, payload)
 return response.data as { status: string; message?: string; data?: unknown }
 },

 getPaymentHistory: async (id: number) => {
 const response = await axios.get(`/customers/${id}/history`)
 return response.data as { status: string; data?: CustomerPaymentRow[]; message?: string }
 },

 getLoyaltyTransactions: async (id: number, params?: { per_page?: number; page?: number }) => {
 const response = await axios.get(`/customers/${id}/loyalty-transactions`, { params })
 return response.data
 },

 adjustLoyaltyPoints: async (id: number, points_delta: number, description?: string) => {
 const response = await axios.post(`/customers/${id}/loyalty/adjust`, { points_delta, description })
 return response.data
 },

 getAddresses: async (id: number) => {
 const response = await axios.get(`/customers/${id}/addresses`)
 return response.data
 },

 addAddress: async (id: number, payload: CustomerAddressPayload) => {
 const response = await axios.post(`/customers/${id}/addresses`, payload)
 return response.data
 },

 updateAddress: async (id: number, addressId: string, payload: Partial<CustomerAddressPayload>) => {
 const response = await axios.put(`/customers/${id}/addresses/${addressId}`, payload)
 return response.data
 },

 deleteAddress: async (id: number, addressId: string) => {
 const response = await axios.delete(`/customers/${id}/addresses/${addressId}`)
 return response.data
 },

 setDefaultAddress: async (id: number, addressId: string) => {
 const response = await axios.post(`/customers/${id}/addresses/${addressId}/default`)
 return response.data
 },

 getPhones: async (id: number) => {
 const response = await axios.get(`/customers/${id}/phones`)
 return response.data
 },

 addPhone: async (id: number, payload: CustomerPhonePayload) => {
 const response = await axios.post(`/customers/${id}/phones`, payload)
 return response.data
 },

 updatePhone: async (id: number, phoneId: string, payload: Partial<CustomerPhonePayload>) => {
 const response = await axios.put(`/customers/${id}/phones/${phoneId}`, payload)
 return response.data
 },

 deletePhone: async (id: number, phoneId: string) => {
 const response = await axios.delete(`/customers/${id}/phones/${phoneId}`)
 return response.data
 },

 setDefaultPhone: async (id: number, phoneId: string) => {
 const response = await axios.post(`/customers/${id}/phones/${phoneId}/default`)
 return response.data
 },

 lookupForPos: async (phone: string) => {
 const response = await axios.get('/pos/customers/lookup', { params: { phone } })
 return response.data as { status: string; data: PosCustomerLookupData }
 },

 quickCreateForPos: async (payload: {
 name: string
 phone: string
 additional_phone?: string
 email?: string
 notes?: string
 address?: {
 label?: string
 address_line_1: string
 address_line_2?: string
 city?: string
 area?: string
 notes?: string
 zone_id?: string
 }
 }) => {
 const response = await axios.post('/pos/customers/quick-create', payload)
 return response.data as {
 status: string
 message?: string
 data: {
 reused_existing?: boolean
 customer: Record<string, unknown>
 selected_address: Record<string, unknown> | null
 }
 }
 },

 addDeliveryAddressForPos: async (customerId: number, payload: Record<string, unknown>) => {
 const response = await axios.post(`/pos/customers/${customerId}/addresses`, payload)
 return response.data
 },
}

export function formatCustomerAddressLine(addr?: CustomerAddressSummary | null): string {
 if (!addr?.address_line_1) return '—'
 const parts = [addr.label, addr.address_line_1, addr.address_line_2, addr.area, addr.city].filter(
 (p) => p != null && String(p).trim() !== ''
 )
 return parts.join(' — ')
}
